import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, RefreshCw, MessageSquare, ShieldCheck, DollarSign, CheckCircle2, Lock } from "lucide-react";
import { EmployeePanel } from "@/components/EmployeePanel";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { SENSITIVE_ACTIONS } from "@/data/seed";
import { toast } from "sonner";

const STATUS_UI = {
  pending: { label: "Waiting for approval", color: "#FFBF00" },
  approved: { label: "Authorized", color: "#22D3EE" },
  rejected: { label: "Rejected", color: "#FF0055" },
  changes: { label: "Changes requested", color: "#8A2BE2" },
};

export default function ApprovalCenter() {
  const { state, resolveApproval, addActivity } = useStore();
  const { user } = useAuth();
  const canApprove = user?.role === "admin";
  const [panelId, setPanelId] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const act = (a, status, verb) => {
    resolveApproval(a.id, status);
    addActivity({ employee: a.recommendedBy, task: `${verb}: ${a.title}`, source: "Approval Center", action: `Lewis ${verb.toLowerCase()} ${a.detail}`, consulted: [a.preparedBy], approval: status === "approved" ? "Granted" : status, result: STATUS_UI[status].label, icon: status === "approved" ? "CheckCircle2" : "ShieldQuestion" });
    if (status === "approved") { setConfirm(a); setTimeout(() => setConfirm(null), 2600); }
    toast.success(`${verb} — ${a.title}`, { description: STATUS_UI[status].label });
  };

  const pending = state.approvals.filter((a) => a.status === "pending");

  return (
    <div>
      {panelId && <EmployeePanel employeeId={panelId} initialMode="talk" onClose={() => setPanelId(null)} />}

      <AnimatePresence>
        {confirm && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="fixed left-1/2 top-6 z-[70] -translate-x-1/2 rounded-xl border border-[#22D3EE]/40 bg-[#0A1326] px-5 py-3 shadow-2xl" data-testid="approval-confirm">
            <div className="flex items-center gap-3"><CheckCircle2 className="text-[#22D3EE]" /><div><div className="text-sm font-medium text-white">Service request authorized</div><div className="text-xs text-slate-400">{confirm.detail} · logged to activity</div></div></div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">Approval Center</h1>
        <p className="mt-1 text-sm text-slate-400">Decisions that require your authorization · {pending.length} waiting</p>
      </div>

      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#FFBF00]/20 bg-[#FFBF00]/5 p-4">
        <Lock size={18} className="mt-0.5 shrink-0 text-[#FFBF00]" />
        <div className="text-sm text-slate-300">
          <span className="text-white">Sensitive actions always require you.</span> AI associates can prepare and recommend, but never finalize:
          <span className="ml-1 text-slate-400">{SENSITIVE_ACTIONS.join(" · ")}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {state.approvals.map((a, i) => {
          const ui = STATUS_UI[a.status];
          const resolved = a.status !== "pending";
          return (
            <motion.div key={a.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="glass rounded-2xl p-5" data-testid={`approval-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-lg font-semibold text-white">{a.title}</h2>
                  <div className="text-sm text-slate-400">{a.detail}</div>
                </div>
                <span className="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-mono uppercase" style={{ borderColor: `${ui.color}55`, color: ui.color, background: `${ui.color}12` }} data-testid={`approval-status-${a.id}`}>{ui.label}</span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {Object.entries(a.fields).map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-white/8 bg-[#030A16]/40 p-2.5"><div className="text-[10px] font-mono uppercase text-slate-500">{k}</div><div className="text-sm text-slate-200">{v}</div></div>
                ))}
                <div className="rounded-lg border border-[#22D3EE]/20 bg-[#22D3EE]/5 p-2.5"><div className="text-[10px] font-mono uppercase text-slate-500">Estimated cost</div><div className="flex items-center gap-1 text-sm text-[#22D3EE]"><DollarSign size={13} />{a.cost}</div></div>
              </div>

              {!resolved ? (
                canApprove ? (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button onClick={() => act(a, "approved", "Approved")} className="flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-[#22D3EE] to-[#00E5FF] px-3 py-2 text-sm font-semibold text-[#030A16]" data-testid={`approve-${a.id}`}><Check size={15} /> Approve</button>
                    <button onClick={() => act(a, "rejected", "Rejected")} className="flex items-center justify-center gap-1.5 rounded-lg border border-[#FF0055]/40 px-3 py-2 text-sm text-[#FF6C93] hover:bg-[#FF0055]/10" data-testid={`reject-${a.id}`}><X size={15} /> Reject</button>
                    <button onClick={() => act(a, "changes", "Requested changes")} className="flex items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 hover:border-[#8A2BE2]/50" data-testid={`changes-${a.id}`}><RefreshCw size={14} /> Request Changes</button>
                    <button onClick={() => setPanelId(a.discussWith)} className="flex items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 hover:border-[#00E5FF]/40" data-testid={`discuss-${a.id}`}><MessageSquare size={14} /> Discuss</button>
                  </div>
                ) : (
                  <div className="mt-4" data-testid={`viewonly-${a.id}`}>
                    <div className="flex items-center gap-2 rounded-lg border border-[#FFBF00]/25 bg-[#FFBF00]/5 px-3 py-2.5 text-sm text-slate-300">
                      <Lock size={15} className="text-[#FFBF00]" /> View only — sign in as an admin to authorize decisions.
                    </div>
                    <button onClick={() => setPanelId(a.discussWith)} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 hover:border-[#00E5FF]/40" data-testid={`discuss-${a.id}`}><MessageSquare size={14} /> Discuss</button>
                  </div>
                )
              ) : (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-white/8 bg-[#030A16]/40 px-3 py-2.5 text-sm text-slate-300"><ShieldCheck size={15} style={{ color: ui.color }} /> {ui.label} · recorded in the activity log</div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
